// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { Helmet } from "react-helmet";
import { isEmpty, omitBy, find, isUndefined } from "lodash";

// ANT COMPONENT
import { Button, Pagination, Tabs } from "@life-line-lab/components-react";
import {
  LeftOutlined,
  RightCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";

// COMPONENT
import CustomerLocationListComponent from "Components/customer-management/customer-location-list";
import CustomerLocationDetailsComponent from "Components/customer-management/customer-location-details";
import CustomerLocationForm from "Components/customer-management/customer-location-form";

// HOOKS
import useQuery from "Hooks/query.hook";
import { useAccessControlList, useItemsPerPage, useSession } from "Hooks/index";
import usePagination from "Hooks/pagination.hook";

// HELPERS
import { decode, encode } from "Helpers/enc";
import { toaster } from "Helpers/toaster";

// SERVICES
import {
  createCustomerLocation,
  deleteCustomerLocation,
  fetchCustomerLocation,
  fetchCustomerLocationList,
  restoreCustomerLocation,
  updateCustomerLocation,
} from "Services/location.service";
import { fetchCustomer, fetchCustomerList } from "Services/customer.service";
import { reverseGeocoding } from "Services/geocoding.services";

// TYPES
import { Sorting, SortingMode } from "../../../types/common.types";
import { CustomerLocationFormType } from "Types/customer_location.types";
import { CustomerCompanyFormInputType } from "Types/customer-form.types";

// CONTANTS
import { DEFAULT_MAX_PER_PAGE } from "Components/shared/constants";

// ATOM
import {
  customerLocationAtom,
  CustomerLocationAtomType,
} from "Recoil/customer_location.atom";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import ConfirmStatus from "Components/shared/confirm-status";
import response from "Utils/normalizer/response";

type ComponentStateType = "List" | "Details" | "Form";

const toNum = (data) => {
  if (typeof data === "string") {
    if (data.match(/^[0-9]+$/)) {
      return parseInt(data);
    } else {
      return 1;
    }
  }
  return data;
};

const CustomerLocationPage = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const query = useQuery();

  const { org, roleTypes } = useSession();

  const location = useLocation();

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Customer_Location_Management",
  });

  const customerPermission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Customer_Management",
  });

  const isReadonly =
    !permission.rights.includes("CREATE") &&
    !permission.rights.includes("UPDATE");
  const canViewCustomerList = customerPermission.rights.includes("LIST");

  const page = query.get("page") || 1;
  const [currentListCount, setCurrentListCount] = useState(0);

  const initialQueryParams = {
    page,
    offset: (toNum(page) - 1) * toNum(itemPerPage),
  };
  const initialSorting = { sortBy: "orgName", mode: SortingMode.DESC };
  const listType =
    location.pathname.indexOf("/customer-management/location/deleted") > -1
      ? "Deleted"
      : "Normal";
  let initialFilters = { tempDelete: listType === "Deleted" };

  const { id } = useParams<{ id: string }>();
  const locationID = decode(id) || "";

  const [forceRender, setForceRender] = useState<boolean>(false);

  const [filters, setFilters] = useState<{
    [key: string]: string | boolean | undefined;
  }>(initialFilters);

  const { paginationComponent, itemPerPage, queryParams, setQueryParams } =
    usePagination({
      currentListCount,
      currentPath: location.pathname,
      resetPageDeps: { filters },
    });

  const [sorting, setSorting] = useState<Sorting>(initialSorting);
  const [componentState, setComponentState] =
    useState<ComponentStateType>("List");
  const [locationList, setLocationList] = useState<any[]>([]);
  const [customerList, setCustomerList] = useState<any[]>([]);
  const [locationDetails, setLocationDetails] = useState<
    Partial<CustomerLocationFormType>
  >({});
  const [customerDetails, setCustomerDetails] = useState<
    Partial<CustomerCompanyFormInputType>
  >({});
  const globalFilterValue = useRecoilValue(GlobalFilterAtom);
  const [selectedOrgIDs, setSelectedOrgIDs] = useState<string[]>([]);
  const [, setCustomerLocationStatus] =
    useRecoilState<CustomerLocationAtomType>(customerLocationAtom);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPrompt, setShowPrompt] = useState<boolean>(false);

  useEffect(() => {
    const path = location.pathname;
    const decodedLocationID = decode(id);

    if (path.indexOf("create") > -1) {
      setComponentState("Form");
    } else if (id) {
      if (decodedLocationID) {
        if (path.indexOf("view") > -1) {
          console.log("view path");
          setComponentState("Details");
        } else if (path.indexOf("edit") > -1) {
          console.log("edit path");
          setComponentState("Form");
        }
      } else {
        history.replace(`/customer-management/location`);
        //history.goBack();
      }
    }
  }, [location.pathname, id]);

  useEffect(() => {
    let orgIDs: string[] = [];
    if (!isEmpty(globalFilterValue.companyIds)) {
      globalFilterValue?.companyIds?.map((orgSelected) => {
        orgIDs.push(orgSelected);
      });
      setSelectedOrgIDs(orgIDs);
    }
    if (!isEmpty(globalFilterValue.logisticsProviderIds)) {
      globalFilterValue?.logisticsProviderIds?.map((orgSelected) => {
        orgIDs.push(orgSelected);
      });
      setSelectedOrgIDs(orgIDs);
    }
  }, [globalFilterValue]);

  useEffect(() => {
    if (!locationID) {
      handleFetchCustomerLocationList();
    }
  }, [queryParams.offset, filters, sorting, itemPerPage, selectedOrgIDs]);

  useEffect(() => {
    if (locationID) {
      handleFetchCustomerLocationDetails();
    }
  }, [locationID]);

  useEffect(() => {
    if (componentState === "Form" && canViewCustomerList) {
      handleFetchCustomerList();
    }
  }, [componentState]);

  useEffect(() => {
    if (locationDetails.customerID) {
      handleFetchCustomerDetails(locationDetails.customerID);
    }
  }, [locationDetails]);

  const onBack = () => {
    history.goBack();
  };

  const handleSort = async (sortBy) => {
    if (sorting.sortBy === sortBy) {
      let mode = SortingMode.NEITHER;
      switch (sorting.mode) {
        case SortingMode.ASC: {
          mode = SortingMode.DESC;
          break;
        }
        case SortingMode.DESC: {
          mode = SortingMode.ASC;
          break;
        }
        case SortingMode.NEITHER: {
          mode = SortingMode.DESC;
          break;
        }
      }
      setSorting({ ...sorting, mode });
    } else {
      setSorting({ sortBy: sortBy, mode: SortingMode.DESC });
    }
  };

  const handleCreateCustomerLocationAction = () => {
    history.push("/customer-management/location/create");
  };

  const handleViewCustomerLocationDetails = (record) => {
    const id = encode(record._id);
    history.push(`/customer-management/location/${id}/view`);
  };

  const handleEditCustomerLocationDetails = (record) => {
    const id = encode(record._id);
    history.push(`/customer-management/location/${id}/edit`);
  };

  const componentSwitch = () => {
    console.log("componentState", componentState);
    if (componentState === "Details") {
      return (
        <CustomerLocationDetailsComponent
          onBack={() => history.goBack()}
          onEdit={() => handleEditCustomerLocationDetails(locationDetails)}
          locationDetails={locationDetails}
          customerDetails={customerDetails}
          onDeleteLocation={handleDeleteCustomerLocation}
          onRestoreLocation={handleRestoreCustomerLocation}
          onFetchCustomerLocation={handleFetchCustomerLocationDetails}
          isReadonly={isReadonly}
        />
      );
    }
    if (componentState === "Form") {
      return (
        <CustomerLocationForm
          onBack={() => history.goBack()}
          onSubmit={handleCreateCustomerLocation}
          onSubmitEdit={handleUpdateCustomerLocation}
          customerDetails={customerDetails}
          customerList={customerList}
          locationDetails={locationDetails}
          canViewCustomerList={canViewCustomerList}
          isLoading={isLoading}
        />
      );
    }
    if (componentState === "List") {
      return (
        <React.Fragment>
          {listType === "Normal" && !isReadonly ? (
            <div className="action-create-customer-location">
              <Button
                label={t("action.createCustomerLocation")}
                icon={<RightCircleOutlined />}
                type="primary"
                onClick={() => {
                  handleCreateCustomerLocationAction();
                }}
              />
            </div>
          ) : null}
          <CustomerLocationListComponent
            onViewDetails={(record) =>
              handleViewCustomerLocationDetails(record)
            }
            onEdit={(record) => handleEditCustomerLocationDetails(record)}
            listType={listType}
            locationList={locationList}
            onSort={handleSort}
            sorting={sorting}
            onDeleteLocation={handleDeleteCustomerLocation}
            onFetchCustomerLocationList={handleFetchCustomerLocationList}
            onFilter={(filter) => {
              setFilters((prevFilters) => ({ ...prevFilters, ...filter }));
            }}
            isReadonly={isReadonly}
            isLoading={isLoading}
          />

          <div className="my-30">{paginationComponent}</div>
        </React.Fragment>
      );
    }
  };

  const miniAddressNormaliser = (data) => {
    return omitBy(
      {
        street1: data.street1,
        street2: data.street2,
        city: data.city,
        state: data.state,
        postcode: data.postcode,
        country: data.country,
      },
      isUndefined,
    );
  };

  const handleCoordinatesChecking = async (address: any) => {
    const responseData = await reverseGeocoding(address);
    if (responseData && !responseData.error) {
      return responseData.coordinates;
    } else {
      throw new Error("Address does not exist");
    }
  };

  const handleCreateCustomerLocation = async (
    data: CustomerLocationFormType,
  ) => {
    const addressToCheck = miniAddressNormaliser(data);

    try {
      const coordinatesObtained = await handleCoordinatesChecking(
        addressToCheck,
      );

      const finalData = {
        ...data,
        coordinate: coordinatesObtained,
      };

      const responseData = await createCustomerLocation(finalData);

      if (responseData && !responseData.error) {
        toaster("success", t("toaster.createCustomerLocationSuccess"));
        setShowPrompt && setShowPrompt(true);
      } else {
        toaster("error", responseData.error.message);
      }
    } catch (error) {
      toaster("error", "Error in creating customer location");
    }
  };

  const handleUpdateCustomerLocation = async (
    data: CustomerLocationFormType,
  ) => {
    const addressToCheck = miniAddressNormaliser(data);

    try {
      const coordinatesObtained = await handleCoordinatesChecking(
        addressToCheck,
      );

      const finalData = {
        ...data,
        coordinate: coordinatesObtained,
      };
      const responseData = await updateCustomerLocation(finalData);

      if (responseData && !responseData.error) {
        toaster("success", t("toaster.updateCustomerLocationSuccess"));
        setShowPrompt && setShowPrompt(true);
      } else {
        toaster("error", responseData.error.message);
      }
    } catch (error) {
      toaster("error", error);
    }
  };

  const handleFetchCustomerLocationList = async () => {
    //If there is no global filter, set it as own
    let orgIDs: string[] = [];
    orgIDs.push(org._id);

    setIsLoading && setIsLoading(true);

    const responseData = await fetchCustomerLocationList(
      filters,
      // org._id,
      !isEmpty(selectedOrgIDs) ? selectedOrgIDs : orgIDs,
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      sorting,
    );

    if (responseData && !responseData.error) {
      setLocationList(responseData);
      setCurrentListCount(responseData.length);
    } else {
      toaster("error", responseData.error.message);
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchCustomerLocationDetails = async () => {
    setIsLoading && setIsLoading(true);
    const responseData = await fetchCustomerLocation(locationID);

    if (responseData && !responseData.error) {
      setLocationDetails(responseData);
    } else {
      toaster("error", responseData.error.message);
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchCustomerList = async () => {
    setIsLoading && setIsLoading(true);
    const responseData = await fetchCustomerList({}, org._id);

    if (responseData && !responseData.error) {
      setCustomerList(responseData);
    } else {
      toaster("error", responseData.error.message);
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchCustomerDetails = async (orgID: string) => {
    setIsLoading && setIsLoading(true);
    const responseData = await fetchCustomer(orgID);

    if (responseData && !responseData.error) {
      setCustomerDetails(responseData);
    } else {
      toaster("error", responseData.error.message);
    }

    setIsLoading && setIsLoading(false);
  };

  const handleDeleteCustomerLocation = async (
    locationID: string,
    tempDeletedReason: string,
  ) => {
    setCustomerLocationStatus((prevStatus) => ({
      ...prevStatus,
      delete: "loading",
    }));
    const responseData = await deleteCustomerLocation(
      locationID,
      tempDeletedReason,
    );

    if (responseData && !responseData.error) {
      setCustomerLocationStatus((prevStatus) => ({
        ...prevStatus,
        delete: "success",
      }));
    } else {
      setCustomerLocationStatus((prevStatus) => ({
        ...prevStatus,
        delete: "error",
      }));
      toaster("error", responseData.error.message);
    }
  };

  const handleRestoreCustomerLocation = async (locationID: string) => {
    setCustomerLocationStatus((prevStatus) => ({
      ...prevStatus,
      restore: "loading",
    }));
    const responseData = await restoreCustomerLocation(locationID);

    if (responseData && !responseData.error) {
      setCustomerLocationStatus((prevStatus) => ({
        ...prevStatus,
        restore: "success",
      }));
      toaster("success", t("toaster.restoreCustomerLocationSucess"));
    } else {
      setCustomerLocationStatus((prevStatus) => ({
        ...prevStatus,
        restore: "error",
      }));
      toaster("error", responseData.error.message);
    }
  };

  return (
    <div className="customer-management customer-location-page">
      <Helmet>
        <title>{t("pageTitle.customerProfile")}</title>
      </Helmet>
      <div className="title mb-5">{t("title.customerManagement")}</div>
      <div className="subtitle">
        {listType === "Normal"
          ? t("title.customerLocation")
          : t("title.deletedCustomerLocation")}
      </div>
      {showPrompt ? (
        <ConfirmStatus
          title={t("txt.successUpdatedCustomer")}
          subtitle={t("txt.successUpdatedCustomerSubtitle")}
          btnPrm={{
            btnPrmText: t("txt.returnToCustomerLocationList"),
            btnPrmFunc: onBack,
          }}
        />
      ) : (
        componentSwitch()
      )}
    </div>
  );
};

export default CustomerLocationPage;
